import { useEffect, useState } from "react";
import client, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, RefreshCw, CheckCircle2, XCircle, KeyRound, Pencil } from "lucide-react";
import { ProviderIcon } from "@/components/brand-icons";
import { cn } from "@/lib/utils";

export const statusLabel = (c) => {
  if (c.last_test?.ok) return "connected";
  return c.last_test ? "failed" : "untested";
};

export const submitLabel = (saving, isEdit) => {
  if (saving) return "Testing connection…";
  return isEdit ? "Save changes & test" : "Save & test";
};

export const PROVIDERS = [
  { key: "github", label: "GitHub", ownerLabel: "Owner / organization", ownerHint: "e.g. my-org or my-username", baseHint: "https://api.github.com (GitHub Enterprise: https://ghe.corp/api/v3)", tokenHint: "Fine-grained or classic PAT with repo (contents + administration) scope" },
  { key: "gitlab", label: "GitLab", ownerLabel: "Namespace / group", ownerHint: "e.g. platform-team or my-username", baseHint: "https://gitlab.com (self-hosted: https://gitlab.corp)", tokenHint: "Personal access token with api scope" },
  { key: "azure_devops", label: "Azure DevOps", ownerLabel: "Organization/Project", ownerHint: "e.g. contoso/Platform", baseHint: "https://dev.azure.com", tokenHint: "PAT with Code (Read & Write) scope" },
];

const emptyForm = { name: "", provider: "github", token: "", base_url: "", default_owner: "", visibility: "private" };

export function ConnectorForm({ open, onOpenChange, onCreated, editing = null }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const meta = PROVIDERS.find((p) => p.key === form.provider);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = !!editing;

  useEffect(() => {
    if (!open) return;
    setForm(editing ? { name: editing.name, provider: editing.provider, token: "", base_url: editing.base_url || "", default_owner: editing.default_owner || "", visibility: editing.visibility || "private" } : emptyForm);
  }, [open, editing]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const body = { name: form.name, base_url: form.base_url || "", default_owner: form.default_owner, visibility: form.visibility };
        if (form.token.trim()) body.token = form.token.trim();
        const { data } = await client.patch(`/connectors/${editing.id}`, body);
        toast.success(data.last_test?.ok ? `Updated — connected as ${data.last_test.login}` : "Connector updated (connection test failed)");
        onCreated(data);
      } else {
        const { data } = await client.post("/connectors", { ...form, base_url: form.base_url || null });
        toast.success(data.last_test?.ok ? `Connected as ${data.last_test.login}` : "Connector saved (connection test failed)");
        onCreated(data);
      }
      setForm(emptyForm);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="connector-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? `Edit connector · ${editing.name}` : "New Git connector"}</DialogTitle>
          <DialogDescription>{isEdit ? "Leave the token empty to keep the current one. Saving re-runs the connection test." : "Tokens are encrypted at rest and never returned to the browser."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="connector-form">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Platform</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {PROVIDERS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  disabled={isEdit}
                  onClick={() => set("provider", p.key)}
                  className={cn(
                    "flex items-center gap-2 rounded-md border px-3 py-2.5 text-left text-sm transition-colors",
                    form.provider === p.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-400",
                    isEdit && form.provider !== p.key && "opacity-40",
                  )}
                  data-testid={`provider-${p.key}`}
                >
                  <ProviderIcon provider={p.key} className="h-4 w-4" />
                  <span className="font-display font-semibold">{p.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Name</Label>
              <Input required data-testid="connector-name" className="mt-2 h-10 border-slate-200" placeholder="Corp GitHub" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">New repos</Label>
              <Select value={form.visibility} onValueChange={(v) => set("visibility", v)}>
                <SelectTrigger data-testid="connector-visibility" className="mt-2 h-10 border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">{meta.ownerLabel}</Label>
            <Input required={form.provider === "azure_devops"} data-testid="connector-owner" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" placeholder={meta.ownerHint} value={form.default_owner} onChange={(e) => set("default_owner", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Personal access token {isEdit && <span className="normal-case tracking-normal text-slate-400">(current ••••{editing.token_hint} — leave empty to keep)</span>}</Label>
            <Input required={!isEdit} type="password" data-testid="connector-token" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" placeholder={isEdit ? "Paste a new token to rotate it" : ""} value={form.token} onChange={(e) => set("token", e.target.value)} autoComplete="off" />
            <p className="mt-1.5 font-mono-tight text-[11px] text-slate-500">{meta.tokenHint}</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Base URL <span className="normal-case tracking-normal text-slate-400">(optional)</span></Label>
            <Input data-testid="connector-base-url" className="mt-2 h-10 border-slate-200 font-mono-tight text-sm" placeholder={meta.baseHint} value={form.base_url} onChange={(e) => set("base_url", e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="h-10 rounded-md bg-slate-900 text-white hover:bg-slate-800" data-testid="connector-submit">
              <KeyRound className="mr-2 h-4 w-4" /> {submitLabel(saving, isEdit)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ConnectorCard({ c, isAdmin, onChanged, onDeleted, onEdit }) {
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await client.post(`/connectors/${c.id}/test`);
      onChanged({ ...c, last_test: data });
      data.ok ? toast.success(`Connected as ${data.login}`) : toast.error(data.error);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove connector "${c.name}"?`)) return;
    await client.delete(`/connectors/${c.id}`);
    onDeleted(c.id);
    toast.success("Connector removed");
  };
  const ok = c.last_test?.ok;
  return (
    <Card className="rounded-md border border-slate-200 bg-white p-5 shadow-none" data-testid={`connector-${c.name}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-md border border-slate-200 bg-slate-50 text-slate-900">
            <ProviderIcon provider={c.provider} className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-base font-semibold text-slate-900">{c.name}</div>
            <div className="font-mono-tight text-[11px] text-slate-500">{c.provider_label} · {c.base_url}</div>
          </div>
        </div>
        <span
          className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest", ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700")}
          data-testid={`connector-status-${c.name}`}
        >
          {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
          {statusLabel(c)}
        </span>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div>
          <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">target</p>
          <p className="mt-0.5 font-mono-tight text-slate-800">{c.default_owner || "—"}</p>
        </div>
        <div>
          <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">token</p>
          <p className="mt-0.5 font-mono-tight text-slate-800">••••{c.token_hint}</p>
        </div>
        <div>
          <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">identity</p>
          <p className="mt-0.5 font-mono-tight text-slate-800">{c.last_test?.login || "—"}</p>
        </div>
        <div>
          <p className="font-mono-tight text-[10px] uppercase tracking-widest text-slate-500">new repos</p>
          <p className="mt-0.5 font-mono-tight text-slate-800">{c.visibility}</p>
        </div>
      </div>
      {c.last_test && !ok && (
        <p className="mt-3 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 font-mono-tight text-[11px] text-amber-800" data-testid={`connector-error-${c.name}`}>
          {c.last_test.error}
        </p>
      )}
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        {isAdmin ? (
          <Button variant="ghost" size="sm" onClick={test} disabled={busy} className="h-8 text-slate-600" data-testid={`connector-test-${c.name}`}>
            <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", busy && "animate-spin")} /> Test connection
          </Button>
        ) : (
          <span className="font-mono-tight text-[11px] text-slate-400">last test: {c.last_test?.at ? new Date(c.last_test.at).toLocaleDateString() : "—"}</span>
        )}
        {isAdmin && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => onEdit(c)} className="h-8 text-slate-600" data-testid={`connector-edit-${c.name}`}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
            </Button>
            <Button variant="ghost" size="icon" onClick={remove} className="h-8 w-8" data-testid={`connector-delete-${c.name}`}>
              <Trash2 className="h-4 w-4 text-slate-500" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
